import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';

import { ApiService } from '../../core/services/api.service';
import { PmAiApiService } from './pm-ai.service';
import { AI_PM_ACTIONS, AiPmSuggestionDto } from './pm-ai.models';

interface ProjectRow { id: string; name: string; }
interface IssueRow { id: string; key: string; summary: string; }
interface SprintRow { id: string; name: string; }
interface VersionRow { id: string; name: string; }

export interface ParsedDraft {
  summary: string;
  typeLabel: string;
  priorityLabel: string;
  priorityClass: string;
}

export interface ParsedSection {
  title: string;
  items: string[];
}

export interface ParsedKv {
  label: string;
  value: string;
}

export interface ParsedResultView {
  title?: string;
  markdown?: string;
  paragraphs: string[];
  drafts: ParsedDraft[];
  sections: ParsedSection[];
  stats: ParsedKv[];
  bullets: string[];
  rawFallback?: string;
}

const ISSUE_TYPES = ['Task', 'Bug', 'Story', 'Epic', 'Improvement', 'Sub-task', 'Feature', 'Tech debt', 'Spike', 'Requirement', 'Research', 'Change request', 'Initiative', 'Portfolio'];
const ISSUE_PRIORITIES = ['Lowest', 'Low', 'Medium', 'High', 'Highest'];

@Component({
  selector: 'app-pm-ai',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatButtonModule, MatFormFieldModule, MatSelectModule, MatInputModule,
    MatIconModule, MatProgressSpinnerModule, MatTabsModule, MatTooltipModule,
    MatChipsModule
  ],
  templateUrl: './pm-ai.component.html',
  styleUrl: './pm-ai.component.scss'
})
export class PmAiComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly ai = inject(PmAiApiService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly result = signal<AiPmSuggestionDto | null>(null);
  readonly projects = signal<ProjectRow[]>([]);
  readonly issues = signal<IssueRow[]>([]);
  readonly sprints = signal<SprintRow[]>([]);
  readonly versions = signal<VersionRow[]>([]);
  readonly actions = AI_PM_ACTIONS;

  readonly parsed = computed(() => this.parseOutput(this.result()?.outputJson));

  selectedProjectId = '';
  selectedIssueId = '';
  selectedSprintId = '';
  selectedVersionId = '';
  prompt = '';
  activeAction = 'daily-summary';

  ngOnInit(): void {
    this.api.getPaged<ProjectRow>('v1/Projects', { page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        this.projects.set(page.items ?? []);
        if (page.items?.[0]) this.selectProject(page.items[0].id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to load projects.')
    });
  }

  selectProject(id: string): void {
    this.selectedProjectId = id;
    this.result.set(null);
    this.api.getPaged<IssueRow>('v1/issues', { page: 1, pageSize: 50, projectId: id }).subscribe({
      next: (page) => {
        this.issues.set(page.items ?? []);
        this.selectedIssueId = page.items?.[0]?.id ?? '';
      }
    });
    this.api.get<SprintRow[]>(`v1/sprints`, { projectId: id }).subscribe({
      next: (rows) => {
        const list = Array.isArray(rows) ? rows : [];
        this.sprints.set(list);
        this.selectedSprintId = list[0]?.id ?? '';
      },
      error: () => this.sprints.set([])
    });
    this.api.get<VersionRow[]>(`v1/pm/versions`, { projectId: id }).subscribe({
      next: (rows) => {
        const list = Array.isArray(rows) ? rows : [];
        this.versions.set(list);
        this.selectedVersionId = list[0]?.id ?? '';
      },
      error: () => this.versions.set([])
    });
  }

  run(): void {
    if (!this.selectedProjectId) {
      this.error.set('Select a project.');
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.result.set(null);

    const done = {
      next: (r: AiPmSuggestionDto) => {
        this.result.set(r);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.loading.set(false);
        this.error.set(err?.message || 'AI request failed.');
      }
    };

    switch (this.activeAction) {
      case 'suggest-tasks':
        this.ai.suggestTasks(this.selectedProjectId, this.prompt || 'New work items').subscribe(done);
        break;
      case 'generate-story':
        this.ai.generateStory(this.selectedProjectId, this.prompt || 'User story').subscribe(done);
        break;
      case 'acceptance-criteria':
        if (!this.selectedIssueId) { this.loading.set(false); this.error.set('Select an issue.'); return; }
        this.ai.acceptanceCriteria(this.selectedIssueId).subscribe(done);
        break;
      case 'predict-risk':
        if (!this.selectedIssueId) { this.loading.set(false); this.error.set('Select an issue.'); return; }
        this.ai.predictRisk(this.selectedIssueId).subscribe(done);
        break;
      case 'predict-deadline':
        if (!this.selectedIssueId) { this.loading.set(false); this.error.set('Select an issue.'); return; }
        this.ai.predictDeadline(this.selectedIssueId).subscribe(done);
        break;
      case 'duplicates':
        if (!this.selectedIssueId) { this.loading.set(false); this.error.set('Select an issue.'); return; }
        this.ai.duplicates(this.selectedIssueId).subscribe(done);
        break;
      case 'daily-summary':
        this.ai.dailySummary(this.selectedProjectId).subscribe(done);
        break;
      case 'weekly-report':
        this.ai.weeklyReport(this.selectedProjectId).subscribe(done);
        break;
      case 'standup':
        this.ai.standup(this.selectedProjectId).subscribe(done);
        break;
      case 'sprint-plan':
        if (!this.selectedSprintId) { this.loading.set(false); this.error.set('Select a sprint.'); return; }
        this.ai.planSprint(this.selectedSprintId).subscribe(done);
        break;
      case 'release-notes':
        if (!this.selectedVersionId) { this.loading.set(false); this.error.set('Select a version.'); return; }
        this.ai.releaseNotes(this.selectedVersionId).subscribe(done);
        break;
      case 'categorize-bug':
        if (!this.selectedIssueId) { this.loading.set(false); this.error.set('Select an issue.'); return; }
        this.ai.categorizeBug(this.selectedIssueId).subscribe(done);
        break;
      default:
        this.loading.set(false);
        this.error.set('Unknown action.');
    }
  }

  apply(): void {
    const r = this.result();
    if (!r?.id) return;
    this.loading.set(true);
    this.ai.apply(r.id).subscribe({
      next: (updated) => {
        this.result.set(updated);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Apply failed.');
      }
    });
  }

  needsPrompt(): boolean {
    return this.actions.find(a => a.id === this.activeAction)?.needs === 'prompt';
  }

  needsIssue(): boolean {
    return this.actions.find(a => a.id === this.activeAction)?.needs === 'issue';
  }

  needsSprint(): boolean {
    return this.actions.find(a => a.id === this.activeAction)?.needs === 'sprint';
  }

  needsVersion(): boolean {
    return this.actions.find(a => a.id === this.activeAction)?.needs === 'version';
  }

  private parseOutput(raw?: string | null): ParsedResultView | null {
    if (!raw) return null;

    let data: Record<string, unknown>;
    try {
      data = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {
        paragraphs: [raw],
        drafts: [],
        sections: [],
        stats: [],
        bullets: []
      };
    }

    const view: ParsedResultView = {
      paragraphs: [],
      drafts: [],
      sections: [],
      stats: [],
      bullets: []
    };

    if (typeof data['markdown'] === 'string' && data['markdown'].trim()) {
      // Prefer structured sections when present (avoids showing the same content twice).
      const hasStructuredLists =
        Array.isArray(data['doneYesterday']) ||
        Array.isArray(data['inProgress']) ||
        Array.isArray(data['blockers']) ||
        Array.isArray(data['assignees']) ||
        data['created'] != null ||
        data['completed'] != null;
      if (!hasStructuredLists) {
        view.markdown = this.simpleMarkdown(String(data['markdown']));
      }
    }

    if (typeof data['summary'] === 'string' && data['summary'].trim()) {
      view.title = String(data['summary']);
    }

    const drafts = data['drafts'];
    if (Array.isArray(drafts)) {
      view.drafts = drafts.map((d) => {
        const row = (d ?? {}) as Record<string, unknown>;
        const type = Number(row['type'] ?? 0);
        const priority = Number(row['priority'] ?? 2);
        return {
          summary: String(row['summary'] ?? 'Untitled'),
          typeLabel: ISSUE_TYPES[type] ?? `Type ${type}`,
          priorityLabel: ISSUE_PRIORITIES[priority] ?? `Priority ${priority}`,
          priorityClass: this.priorityClass(priority)
        };
      });
    }

    const ac = data['acceptanceCriteria'];
    if (Array.isArray(ac) && ac.length) {
      view.sections.push({
        title: 'Acceptance criteria',
        items: ac.map((x) => String(x))
      });
    }

    const reasons = data['reasons'];
    if (Array.isArray(reasons) && reasons.length) {
      view.sections.push({
        title: 'Reasons',
        items: reasons.map((x) => String(x))
      });
    }

    if (data['riskLevelLabel'] != null || data['score'] != null) {
      view.title = view.title ?? `Risk: ${data['riskLevelLabel'] ?? 'Unknown'}`;
      if (data['score'] != null) view.stats.push({ label: 'Score', value: String(data['score']) });
      if (data['riskLevelLabel'] != null) view.stats.push({ label: 'Level', value: String(data['riskLevelLabel']) });
    }

    if (data['predictedDate'] != null || data['predictedDays'] != null) {
      view.title = view.title ?? 'Deadline prediction';
      if (data['predictedDate'] != null) view.stats.push({ label: 'Predicted date', value: String(data['predictedDate']) });
      if (data['predictedDays'] != null) view.stats.push({ label: 'Predicted days', value: String(data['predictedDays']) });
      if (data['remainingPoints'] != null) view.stats.push({ label: 'Remaining points', value: String(data['remainingPoints']) });
      if (data['medianDaysPerPoint'] != null) view.stats.push({ label: 'Days per point', value: String(data['medianDaysPerPoint']) });
      if (data['basedOnSamples'] != null) view.stats.push({ label: 'Based on samples', value: String(data['basedOnSamples']) });
    }

    const duplicates = data['duplicates'];
    if (Array.isArray(duplicates)) {
      view.sections.push({
        title: duplicates.length ? 'Possible duplicates' : 'No strong duplicates found',
        items: duplicates.map((d) => {
          const row = (d ?? {}) as Record<string, unknown>;
          const sim = row['similarity'] != null ? ` (${Math.round(Number(row['similarity']) * 100)}% similar)` : '';
          return `${row['key'] ?? ''} — ${row['summary'] ?? ''}${sim}`.trim();
        })
      });
    }

    for (const key of ['doneYesterday', 'inProgress', 'blockers'] as const) {
      const list = data[key];
      if (Array.isArray(list)) {
        const titles: Record<string, string> = {
          doneYesterday: 'Yesterday done',
          inProgress: 'In progress',
          blockers: 'Blockers'
        };
        view.sections.push({
          title: titles[key],
          items: list.length ? list.map((x) => String(x)) : ['(none)']
        });
      }
    }

    const assignees = data['assignees'];
    if (Array.isArray(assignees)) {
      view.sections.push({
        title: 'Standup by assignee',
        items: assignees.map((a) => {
          const row = (a ?? {}) as Record<string, unknown>;
          return `${row['assignee'] ?? 'Unassigned'}: ${row['script'] ?? ''}`.trim();
        })
      });
    }

    if (data['capacity'] != null || data['suggested'] != null) {
      view.title = view.title ?? 'Sprint plan suggestion';
      if (data['capacity'] != null) view.stats.push({ label: 'Capacity', value: String(data['capacity']) });
      if (data['committed'] != null) view.stats.push({ label: 'Committed', value: String(data['committed']) });
      if (data['remainingCapacity'] != null) view.stats.push({ label: 'Remaining', value: String(data['remainingCapacity']) });
      if (data['suggestedPoints'] != null) view.stats.push({ label: 'Suggested points', value: String(data['suggestedPoints']) });
      const suggested = data['suggested'];
      if (Array.isArray(suggested)) {
        view.sections.push({
          title: 'Suggested backlog items',
          items: suggested.length
            ? suggested.map((s) => {
                const row = (s ?? {}) as Record<string, unknown>;
                const pts = row['storyPoints'] != null ? ` · ${row['storyPoints']} pts` : '';
                return `${row['key'] ?? ''} — ${row['summary'] ?? ''}${pts}`.trim();
              })
            : ['No items fit the remaining capacity.']
        });
      }
    }

    for (const key of ['created', 'completed', 'open', 'overdue'] as const) {
      if (data[key] != null && typeof data[key] !== 'object') {
        const labels: Record<string, string> = {
          created: 'Created',
          completed: 'Completed',
          open: 'Still open',
          overdue: 'Overdue'
        };
        // Avoid duplicating if already covered by markdown-only weekly report stats
        if (!view.stats.some((s) => s.label === labels[key])) {
          view.stats.push({ label: labels[key], value: String(data[key]) });
        }
      }
    }

    if (data['category'] != null || data['categoryLabel'] != null || data['severity'] != null) {
      view.title = view.title ?? 'Bug categorization';
      if (data['categoryLabel'] != null) view.stats.push({ label: 'Category', value: String(data['categoryLabel']) });
      else if (data['category'] != null) view.stats.push({ label: 'Category', value: String(data['category']) });
      if (data['severity'] != null) view.stats.push({ label: 'Severity', value: String(data['severity']) });
      if (data['notes'] != null) view.paragraphs.push(String(data['notes']));
    }

    if (typeof data['notes'] === 'string' && data['notes'].trim() && !view.paragraphs.includes(String(data['notes']))) {
      view.paragraphs.push(String(data['notes']));
    }

    const hasContent =
      !!view.markdown ||
      !!view.title ||
      view.drafts.length > 0 ||
      view.sections.length > 0 ||
      view.stats.length > 0 ||
      view.paragraphs.length > 0 ||
      view.bullets.length > 0;

    if (!hasContent) {
      // Flatten unknown object into simple key/value lines
      for (const [k, v] of Object.entries(data)) {
        if (v == null || typeof v === 'object') continue;
        view.stats.push({ label: this.humanizeKey(k), value: String(v) });
      }
      if (view.stats.length === 0) {
        view.rawFallback = JSON.stringify(data, null, 2);
      }
    }

    return view;
  }

  private simpleMarkdown(md: string): string {
    return md
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/^### (.+)$/gm, '<h4>$1</h4>')
      .replace(/^## (.+)$/gm, '<h3>$1</h3>')
      .replace(/^# (.+)$/gm, '<h2>$1</h2>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/^\|(.+)\|$/gm, (_m, row: string) => {
        if (/^\s*-+\s*(\|\s*-+\s*)+$/.test(row) || /^[\s|:-]+$/.test(row)) return '';
        const cells = row.split('|').map((c) => c.trim()).filter(Boolean);
        return `<div class="md-row">${cells.map((c) => `<span>${c}</span>`).join('')}</div>`;
      })
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>\n?)+/g, (block) => `<ul>${block}</ul>`)
      .replace(/\n{2,}/g, '<br/>')
      .replace(/\n/g, '<br/>');
  }

  private priorityClass(priority: number): string {
    if (priority >= 4) return 'prio-highest';
    if (priority === 3) return 'prio-high';
    if (priority === 2) return 'prio-medium';
    if (priority === 1) return 'prio-low';
    return 'prio-lowest';
  }

  private humanizeKey(key: string): string {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (c) => c.toUpperCase())
      .trim();
  }
}
